<script setup lang="ts">
import { NCheckbox } from 'naive-ui'
import AgentRepoActionButton from './AgentRepoActionButton.vue'
import { computed } from 'vue'
import { useAgentReposStore, isLifecycleInFlight, type AgentRepoStatus } from '../stores/agent_repos'
import { useAsyncAction } from '../composables/useAsyncAction'

// The devcontainer lifecycle action buttons for one repo (start/stop for
// now; reboot/rebuild/rebuild-without-cache/update-CLI join here in later
// PRD slices) — extracted (#844) so AdminAgentRepos.vue stays under the
// 300-line hard limit as this action set grows. One instance per repo (via
// v-for in the parent), so each handler's loading state is a plain
// per-instance ref (via useAsyncAction, #897) rather than a slug-keyed map.
const props = defineProps<{ repo: AgentRepoStatus }>()
const store = useAgentReposStore()

// The agent box runs lifecycle actions one at a time and 409s a second
// request while one is in flight (#1051). Each button's own `loading` ref
// only covers the POST, which now returns 202 in milliseconds — the work
// itself continues for minutes afterwards, so this is what actually keeps
// the buttons blocked for the real duration.
const actionInFlight = computed(() => isLifecycleInFlight(props.repo))

const { loading: starting, run: start } = useAsyncAction(() => store.start(props.repo.slug), {
  successMessage: () => `${props.repo.slug}: start queued`,
  failureLabel: 'Failed to start devcontainer',
})

const { loading: stopping, run: stop } = useAsyncAction(() => store.stop(props.repo.slug), {
  successMessage: () => `${props.repo.slug}: stop queued`,
  failureLabel: 'Failed to stop devcontainer',
})

// Recovery path for a devcontainer whose network itself is broken (#1235) —
// every other action recreates containers onto whatever's left of the same
// network. Runs regardless of container_status: DevcontainerCli#destroy
// finds a stopped primary too, and a broken network can outlive its container.
const { loading: destroying, run: destroyContainer } = useAsyncAction(() => store.destroyContainer(props.repo.slug), {
  successMessage: () => `${props.repo.slug}: destroy queued`,
  failureLabel: 'Failed to remove devcontainer',
})

const { loading: rebooting, run: reboot } = useAsyncAction(() => store.reboot(props.repo.slug), {
  successMessage: () => `${props.repo.slug}: reboot queued`,
  failureLabel: 'Failed to reboot devcontainer',
})

const { loading: rebuilding, run: rebuild } = useAsyncAction(() => store.rebuild(props.repo.slug, false), {
  successMessage: () => `${props.repo.slug}: rebuild queued`,
  failureLabel: 'Failed to rebuild devcontainer',
})

const { loading: rebuildingNoCache, run: rebuildNoCache } = useAsyncAction(() => store.rebuild(props.repo.slug, true), {
  successMessage: () => `${props.repo.slug}: rebuild (no cache) queued`,
  failureLabel: 'Failed to rebuild devcontainer',
})

// Non-destructive — doesn't kill an in-flight session, so no confirm-if-
// active dialog needed (#847).
//
// Every message below says "queued", not "done": these POSTs return 202 and
// the work runs for minutes afterwards on the agent box (#1051). The
// outcome shows up in the Last action row of the status grid.
const { loading: updatingCli, run: updateCli } = useAsyncAction(() => store.updateCli(props.repo.slug), {
  successMessage: () => `${props.repo.slug}: CLI update queued`,
  failureLabel: 'Failed to update Claude Code CLI',
})

// Opts this repo out of (or back into) the nightly AgentCliAutoUpdateJob
// (#896) — the manual "Update Claude Code CLI" button above is unaffected
// either way. No successMessage — this toggle stays silent on success.
const { loading: togglingAutoUpdate, run: toggleAutoUpdate } = useAsyncAction(
  (checked: boolean) => store.update(props.repo.slug, { auto_update_cli: checked }),
  { failureLabel: 'Failed to update auto-update setting' },
)
</script>

<template>
  <AgentRepoActionButton
    v-if="repo.container_status === 'stopped'"
    label="Start"
    :slug="repo.slug"
    :test-id="`agent-repo-start-${repo.slug}`"
    :loading="starting"
    :disabled="actionInFlight"
    button-type="primary"
    @run="start"
  />
  <AgentRepoActionButton
    v-else
    label="Stop"
    :slug="repo.slug"
    :test-id="`agent-repo-stop-${repo.slug}`"
    :loading="stopping"
    :disabled="actionInFlight"
    destructive
    :warn-active="repo.container_status === 'running'"
    tertiary
    @run="stop"
  />
  <AgentRepoActionButton
    v-if="repo.container_status !== 'stopped'"
    label="Update Claude Code CLI"
    :slug="repo.slug"
    :test-id="`agent-repo-update-cli-${repo.slug}`"
    :loading="updatingCli"
    :disabled="actionInFlight"
    @run="updateCli"
  />
  <NCheckbox
    :checked="repo.auto_update_cli"
    :disabled="togglingAutoUpdate"
    :data-testid="`agent-repo-auto-update-${repo.slug}`"
    @update:checked="toggleAutoUpdate"
    >Nightly auto-update</NCheckbox
  >
  <!-- basis-full forces a wrap in the parent's flex-wrap row, splitting
       routine lifecycle controls from the destructive maintenance set. -->
  <div class="basis-full border-t border-border-divider pt-2 mt-1 text-xs text-ink-faint">Destructive maintenance</div>
  <AgentRepoActionButton
    label="Destroy"
    :slug="repo.slug"
    :test-id="`agent-repo-destroy-${repo.slug}`"
    :loading="destroying"
    :disabled="actionInFlight"
    destructive
    warn-active
    confirm-message="This removes the container(s) and, for a compose devcontainer, its network. The workspace checkout is not affected. Continue?"
    button-type="error"
    tertiary
    @run="destroyContainer"
  />
  <AgentRepoActionButton
    v-if="repo.container_status !== 'stopped'"
    label="Reboot"
    :slug="repo.slug"
    :test-id="`agent-repo-reboot-${repo.slug}`"
    :loading="rebooting"
    :disabled="actionInFlight"
    destructive
    :warn-active="repo.container_status === 'running'"
    tertiary
    @run="reboot"
  />
  <AgentRepoActionButton
    v-if="repo.container_status !== 'stopped'"
    label="Rebuild"
    :slug="repo.slug"
    :test-id="`agent-repo-rebuild-${repo.slug}`"
    :loading="rebuilding"
    :disabled="actionInFlight"
    destructive
    :warn-active="repo.container_status === 'running'"
    tertiary
    @run="rebuild"
  />
  <AgentRepoActionButton
    v-if="repo.container_status !== 'stopped'"
    label="Rebuild (no cache)"
    :slug="repo.slug"
    :test-id="`agent-repo-rebuild-no-cache-${repo.slug}`"
    :loading="rebuildingNoCache"
    :disabled="actionInFlight"
    destructive
    :warn-active="repo.container_status === 'running'"
    tertiary
    @run="rebuildNoCache"
  />
</template>
